import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';
import api from '../utils/api';
import { createSanitizedMarkup } from '../utils/sanitize';

const About = () => {
  const { t, language } = useLanguage();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await api.get('/pages/hakkimizda');
        setPage(response.data);
      } catch (error) {
        console.error('Error fetching page:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, []);

  const title = page ? (language === 'tr' ? page.title_tr : page.title_en) : t.nav.about;
  const content = page ? (language === 'tr' ? page.content_tr : page.content_en) : '';

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="about-page">
      <SEO 
        title="Hakkımızda"
        description="BrightMind Software hakkında - Kurumsal yazılım ve dijital dönüşüm alanında uzman ekibimizle tanışın."
        keywords="hakkımızda, BrightMind Software, yazılım şirketi, dijital dönüşüm"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-bms-light via-white to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal>
            <div className="text-center">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-bms-text-light hover:text-bms-primary mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t.pages.backToHome}
              </Link>
              <h1 className="text-3xl md:text-5xl font-bold text-bms-text mb-4">
                {title}
              </h1>
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        {loading ? (
          <div className="text-center text-bms-text-light">{t.pages.loading}</div>
        ) : (
          <ScrollReveal>
            <div className="bg-white rounded-2xl p-8 md:p-12 border border-gray-100 shadow-corporate">
              {content ? (
                <div 
                  className="prose prose-lg max-w-none text-bms-text-light
                           prose-headings:text-bms-text prose-headings:font-bold
                           prose-p:leading-relaxed
                           prose-a:text-bms-primary prose-a:no-underline hover:prose-a:underline
                           prose-strong:text-bms-text"
                  dangerouslySetInnerHTML={createSanitizedMarkup(content)}
                />
              ) : (
                <div className="space-y-6">
                  <p className="text-bms-text-light leading-relaxed">
                    BrightMind Software olarak, 2020 yılından bu yana işletmelerin dijital dönüşüm yolculuklarında 
                    güvenilir bir partner olarak hizmet vermekteyiz.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-6 mt-8">
                    {[
                      "Kurumsal yazılım çözümleri",
                      "Yapay zeka entegrasyonları",
                      "Ölçeklenebilir sistemler",
                      "7/24 teknik destek"
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-bms-primary flex-shrink-0" />
                        <span className="text-bms-text">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollReveal>
        )}

        {/* CTA */}
        <ScrollReveal delay={0.2}>
          <div className="mt-12 text-center">
            <p className="text-bms-text-light mb-6">Projeleriniz için bizimle iletişime geçin</p>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/iletisim"
                className="inline-flex items-center gap-2 px-8 py-4 bg-bms-primary hover:bg-bms-primary-dark text-white rounded-lg font-semibold transition-all"
              >
                {t.nav.getInTouch}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
};

export default About;
